import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store";
import axios from "axios";
import {
  Play,
  Users,
  Layers,
  Download,
  Settings,
  LogOut,
  Plus,
  ArrowRight,
  TrendingUp,
  Crown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = "/api";

const AdminNavbar = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/assets/logo-creavideos.png" 
                alt="CreaVideos" 
                className="h-8 w-auto"
              />
            </Link>
            <span className="px-2 py-1 rounded bg-teal-600 text-xs font-semibold">ADMIN</span>
          </div>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="text-white/80 hover:text-white transition-colors font-medium">
              Dashboard
            </Link>
            <Link to="/admin/templates" className="text-white/80 hover:text-white transition-colors font-medium">
              Templates
            </Link>
            <Link to="/admin/users" className="text-white/80 hover:text-white transition-colors font-medium">
              Users
            </Link>
            <Link to="/admin/user-stats" className="text-white/80 hover:text-white transition-colors font-medium">
              User Stats
            </Link>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-2 rounded-full hover:bg-white/10">
                <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center">
                  <span className="text-sm font-medium">{user?.name?.[0]}</span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/dashboard" className="cursor-pointer">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  User Dashboard
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="cursor-pointer text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get("/admin/stats");
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: "Total Users", value: stats?.total_users || 0, icon: Users, color: "bg-blue-100 text-blue-600" },
    { label: "Total Templates", value: stats?.total_templates || 0, icon: Layers, color: "bg-teal-100 text-teal-600" },
    { label: "Total Renders", value: stats?.total_renders || 0, icon: Download, color: "bg-green-100 text-green-600" },
    { label: "Active Subscriptions", value: stats?.active_subscriptions || 0, icon: Crown, color: "bg-amber-100 text-amber-600" },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNavbar />

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-1">Admin Dashboard</h1>
              <p className="text-slate-600">Manage templates, users, and settings</p>
            </div>
            <Link to="/admin/templates/new">
              <Button className="btn-primary rounded-full" data-testid="create-template-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </Link>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statCards.map((stat) => (
              <div key={stat.label} className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-sm">{stat.label}</p>
                    <p className="text-3xl font-bold mt-1">
                      {loading ? "..." : stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/admin/templates" className="group">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center mb-4">
                  <Layers className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2 group-hover:text-teal-600 transition-colors">
                  Manage Templates
                </h3>
                <p className="text-slate-600 text-sm">
                  Create, edit, and publish video templates
                </p>
              </div>
            </Link>

            <Link to="/admin/users" className="group">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2 group-hover:text-teal-600 transition-colors">
                  Manage Users
                </h3>
                <p className="text-slate-600 text-sm">
                  View users, manage roles, and subscriptions
                </p>
              </div>
            </Link>

            <Link to="/admin/templates/new" className="group">
              <div className="bg-gradient-to-br from-teal-600 to-cyan-500 rounded-2xl p-6 text-white hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-lg mb-2">
                  Create New Template
                </h3>
                <p className="text-white/80 text-sm">
                  Design a new video template with the editor
                </p>
              </div>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
